import type { MailingLanguage } from "@/nango/types/apps.autoleads/autoleads/AutoLeads"

// Mirrors AUDIT_BLOCK_TYPES server-side, in the order the editor offers them.
export const AUDIT_BLOCK_TYPES = [
  "cover",
  "score_summary",
  "site_today",
  "themes",
  "theme",
  "pagespeed",
  "rank",
  "text",
  "cta",
  "about",
  "page_break",
] as const

export type AuditBlockType = (typeof AUDIT_BLOCK_TYPES)[number]

// Themes a prospect may read (the internal `money` theme is never offered).
export const AUDIT_THEME_KEYS = [
  "google_profile",
  "visibility_geo",
  "visibility_keyword",
  "seo_technical",
  "conversion",
  "speed",
  "design",
  "acquisition",
  "trust",
  "compliance",
  "visibility_ai",
] as const

export type AuditThemeKey = (typeof AUDIT_THEME_KEYS)[number]

export const AUDIT_SEVERITIES = ["high", "medium"] as const
export type AuditSeverity = (typeof AUDIT_SEVERITIES)[number]

export const AUDIT_STRATEGIES = ["mobile", "desktop"] as const
export type AuditStrategy = (typeof AUDIT_STRATEGIES)[number]

// Mirrors AUDIT_FONT_FAMILIES server-side.
export const AUDIT_FONTS = {
  inter: "Inter",
  dm_sans: "DM Sans",
  roboto: "Roboto",
  open_sans: "Open Sans",
  lato: "Lato",
  montserrat: "Montserrat",
  poppins: "Poppins",
  source_serif: "Source Serif 4",
  merriweather: "Merriweather",
  playfair: "Playfair Display",
} as const

export type AuditFont = keyof typeof AUDIT_FONTS

// Mirrors AUDIT_KIND_CHOICES server-side: a short punchy audit, or the whole one.
export const AUDIT_KINDS = ["minimal", "complete"] as const
export type AuditKind = (typeof AUDIT_KINDS)[number]

// Mirrors AUDIT_ORIENTATION_CHOICES server-side.
export const AUDIT_ORIENTATIONS = ["portrait", "landscape"] as const
export type AuditOrientation = (typeof AUDIT_ORIENTATIONS)[number]

// Placeholders a text setting may carry, filled per lead (AUDIT_VARIABLES).
export const AUDIT_VARIABLES = [
  "lead.name",
  "lead.website",
  "lead.address",
  "client.name",
  "client.website",
  "score",
  "issues_count",
  "date",
] as const

// Every setting any block may carry. Each block type uses a subset; an empty
// title means "the PDF's own wording in its language".
export type AuditBlockSettings = {
  title?: string
  subtitle?: string
  intro?: string
  body?: string
  button_label?: string
  url?: string
  show_logo?: boolean
  show_date?: boolean
  show_score?: boolean
  show_details?: boolean
  show_screenshot?: boolean
  show_consequences?: boolean
  // site_today: searches (or AI questions) quoted under each finding.
  examples?: number
  severities?: AuditSeverity[]
  theme_keys?: AuditThemeKey[]
  theme_key?: AuditThemeKey
  strategies?: AuditStrategy[]
  max_items?: number
}

export type AuditBlock = {
  type: AuditBlockType
  enabled: boolean
  settings: AuditBlockSettings
}

export type AuditTemplateStyle = {
  color_primary: string
  color_accent: string
  color_text: string
  color_background: string
  font: AuditFont
  orientation: AuditOrientation
  // Drops the Grezy attribution; honoured only on the Scale plan.
  white_label: boolean
  // One brand, two versions: a short punchy audit, and the whole one.
  blocks_minimal: AuditBlock[]
  blocks_complete: AuditBlock[]
}

export type AuditTemplate = AuditTemplateStyle & {
  id: number
  team_id: number
  name: string
  is_default: boolean
  logo_id: string | null
  logo_url: string
  white_label_available: boolean
  created_at: string
  updated_at: string
}

export type AuditTemplatePayload = Partial<AuditTemplateStyle> & {
  name?: string
  is_default?: boolean
  // Only null is accepted: it removes the logo.
  logo_id?: null
}

export type AuditPreviewPayload = {
  team_id: number
  // Which version of the draft to render.
  kind: AuditKind
  template_id: number | null
  lead_id: number | null
  language?: MailingLanguage
  style: AuditTemplateStyle
}

export type AuditSampleLead = {
  id: number
  name: string
  campaign_name: string
}

// The template a campaign's audits use (both versions); null is the team default.
export type CampaignAuditTemplate = {
  campaign_id: number
  template_id: number | null
}
